import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ArrowUp, ArrowDown, Minus, Target, Download, Calendar } from 'lucide-react';
import { getActivityHistory, getGoalsHistory } from '../utils/api';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area,
  ComposedChart, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, PieChart, Pie, Cell
} from 'recharts';

const AgentPerformance = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all'); // 'all', '4w', '8w', '12w'

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    loadData(token);
  }, [navigate]);

  const loadData = async (token) => {
    try {
      setIsLoading(true);
      const [actRes, goalRes] = await Promise.all([
        getActivityHistory(token),
        getGoalsHistory(token)
      ]);
      if (actRes.success) setActivities(actRes.activities.reverse());
      if (goalRes.success) setGoals(goalRes.goals.reverse());
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (chartData.length === 0) return;
    const headers = ['Week', 'Sales', 'Presentations', 'Door Knocks', 'Phone Calls', 'Leads Worked', 'Premium', 'Referrals Received', 'Referrals Contacted'];
    const rows = chartData.map(d => [d.week, d.sales, d.presentations, d.doorKnocks, d.phoneCalls, d.leadsWorked, d.annualizedPremium.toFixed(2), d.referralsReceived, d.referralsContacted]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported as CSV');
  };

  const calcTotals = (acts) => {
    const t = { leadsWorked: 0, doorKnocks: 0, phoneCalls: 0, presentations: 0, sales: 0, futureAppointments: 0, annualizedPremium: 0, referralsReceived: 0, referralsContacted: 0 };
    (acts || []).forEach(d => {
      if (!d.offDay) Object.keys(t).forEach(k => { t[k] += (d[k] || 0); });
    });
    return t;
  };

  const allChartData = activities.filter(a => a.status === 'submitted').map(a => {
    const totals = calcTotals(a.activities);
    const weekLabel = new Date(a.weekStartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { week: weekLabel, weekStart: a.weekStartDate, ...totals };
  });

  // Apply date range filter
  const chartData = dateRange === 'all' ? allChartData :
    dateRange === '4w' ? allChartData.slice(-4) :
    dateRange === '8w' ? allChartData.slice(-8) :
    allChartData.slice(-12);

  // Build Goals vs Actual data
  const goalsMap = {};
  goals.forEach(g => {
    const t = g.weeklyTotals || {};
    goalsMap[g.weekStartDate] = {
      salesGoal: t.salesGoal || 0,
      presentationsGoal: t.presentationsGoal || 0,
      doorKnocksGoal: t.doorKnocksGoal || 0,
      phoneCallsGoal: t.phoneCallsGoal || 0,
      annualizedPremiumGoal: t.annualizedPremiumGoal || 0,
      leadsGoal: t.leadsGoal || 0,
    };
  });

  const goalsVsActualData = chartData.map(d => ({
    ...d,
    salesGoal: goalsMap[d.weekStart]?.salesGoal || 0,
    presentationsGoal: goalsMap[d.weekStart]?.presentationsGoal || 0,
    doorKnocksGoal: goalsMap[d.weekStart]?.doorKnocksGoal || 0,
    phoneCallsGoal: goalsMap[d.weekStart]?.phoneCallsGoal || 0,
    premiumGoal: goalsMap[d.weekStart]?.annualizedPremiumGoal || 0,
    leadsGoal: goalsMap[d.weekStart]?.leadsGoal || 0,
  }));

  const latestWeek = chartData.length > 0 ? chartData[chartData.length - 1] : null;
  const previousWeek = chartData.length > 1 ? chartData[chartData.length - 2] : null;

  // Advanced Analytics Data
  const conversionData = chartData.map(d => ({
    week: d.week,
    conversionRate: d.presentations > 0 ? ((d.sales / d.presentations) * 100).toFixed(1) : 0,
    leadsToSale: d.leadsWorked > 0 ? ((d.sales / d.leadsWorked) * 100).toFixed(1) : 0,
    callsPerSale: d.sales > 0 ? (d.phoneCalls / d.sales).toFixed(1) : 0,
    knocksPerSale: d.sales > 0 ? (d.doorKnocks / d.sales).toFixed(1) : 0,
  }));

  // Cumulative metrics
  const cumulativeData = chartData.reduce((acc, d, i) => {
    const prev = i > 0 ? acc[i - 1] : { cumSales: 0, cumPremium: 0, cumPres: 0, cumLeads: 0 };
    acc.push({
      week: d.week,
      cumSales: prev.cumSales + d.sales,
      cumPremium: prev.cumPremium + d.annualizedPremium,
      cumPres: prev.cumPres + d.presentations,
      cumLeads: prev.cumLeads + d.leadsWorked,
    });
    return acc;
  }, []);

  // Daily activity heatmap
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayActivity = dayLabels.map((day, i) => {
    let totalSales = 0, totalPres = 0, totalCalls = 0, count = 0;
    activities.filter(a => a.status === 'submitted').forEach(a => {
      const d = a.activities?.[i];
      if (d && !d.offDay) {
        totalSales += d.sales || 0;
        totalPres += d.presentations || 0;
        totalCalls += d.phoneCalls || 0;
        count++;
      }
    });
    return {
      day,
      avgSales: count > 0 ? (totalSales / count).toFixed(1) : 0,
      avgPres: count > 0 ? (totalPres / count).toFixed(1) : 0,
      avgCalls: count > 0 ? (totalCalls / count).toFixed(1) : 0,
      totalSales,
      totalPres,
    };
  });

  // Period comparison (last 4 weeks vs previous 4)
  const recentWeeks = chartData.slice(-4);
  const olderWeeks = chartData.slice(-8, -4);
  const sumMetrics = (arr) => arr.reduce((acc, d) => ({
    sales: acc.sales + d.sales,
    presentations: acc.presentations + d.presentations,
    doorKnocks: acc.doorKnocks + d.doorKnocks,
    phoneCalls: acc.phoneCalls + d.phoneCalls,
    annualizedPremium: acc.annualizedPremium + d.annualizedPremium,
    leadsWorked: acc.leadsWorked + d.leadsWorked,
  }), { sales: 0, presentations: 0, doorKnocks: 0, phoneCalls: 0, annualizedPremium: 0, leadsWorked: 0 });
  const recentTotals = sumMetrics(recentWeeks);
  const olderTotals = olderWeeks.length > 0 ? sumMetrics(olderWeeks) : null;

  // Radar chart data for period comparison
  const maxVal = (key) => Math.max(recentTotals[key], olderTotals?.[key] || 0, 1);
  const radarData = olderTotals ? [
    { metric: 'Sales', recent: (recentTotals.sales / maxVal('sales') * 100).toFixed(0), older: (olderTotals.sales / maxVal('sales') * 100).toFixed(0) },
    { metric: 'Presentations', recent: (recentTotals.presentations / maxVal('presentations') * 100).toFixed(0), older: (olderTotals.presentations / maxVal('presentations') * 100).toFixed(0) },
    { metric: 'Door Knocks', recent: (recentTotals.doorKnocks / maxVal('doorKnocks') * 100).toFixed(0), older: (olderTotals.doorKnocks / maxVal('doorKnocks') * 100).toFixed(0) },
    { metric: 'Phone Calls', recent: (recentTotals.phoneCalls / maxVal('phoneCalls') * 100).toFixed(0), older: (olderTotals.phoneCalls / maxVal('phoneCalls') * 100).toFixed(0) },
    { metric: 'Leads', recent: (recentTotals.leadsWorked / maxVal('leadsWorked') * 100).toFixed(0), older: (olderTotals.leadsWorked / maxVal('leadsWorked') * 100).toFixed(0) },
  ] : [];

  // Activity mix pie chart
  const activityMix = [
    { name: 'Door Knocks', value: recentTotals.doorKnocks, color: '#4169E1' },
    { name: 'Phone Calls', value: recentTotals.phoneCalls, color: '#FFA500' },
    { name: 'Presentations', value: recentTotals.presentations, color: '#E87722' },
    { name: 'Leads Worked', value: recentTotals.leadsWorked, color: '#8b5cf6' },
  ].filter(a => a.value > 0);

  const getTrend = (current, previous) => {
    if (!previous || !current) return { icon: Minus, color: 'text-gray-400', pct: '0%' };
    if (previous === 0) return current > 0 ? { icon: ArrowUp, color: 'text-green-500', pct: '+100%' } : { icon: Minus, color: 'text-gray-400', pct: '0%' };
    const pct = ((current - previous) / previous * 100).toFixed(0);
    if (pct > 0) return { icon: ArrowUp, color: 'text-green-500', pct: `+${pct}%` };
    if (pct < 0) return { icon: ArrowDown, color: 'text-red-500', pct: `${pct}%` };
    return { icon: Minus, color: 'text-gray-400', pct: '0%' };
  };

  const kpiCards = [
    { label: 'Sales', key: 'sales', color: '#0047AB' },
    { label: 'Presentations', key: 'presentations', color: '#E87722' },
    { label: 'Door Knocks', key: 'doorKnocks', color: '#4169E1' },
    { label: 'Phone Calls', key: 'phoneCalls', color: '#FFA500' },
    { label: 'Premium', key: 'annualizedPremium', color: '#22c55e', isCurrency: true },
    { label: 'Leads Worked', key: 'leadsWorked', color: '#8b5cf6' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading performance data...</p>
        </div>
      </div>
    );
  }

  const hasGoals = Object.keys(goalsMap).length > 0;

  return (
    <AgentLayout title="Performance Dashboard">
      <div data-testid="performance-dashboard">
      <div>
        {chartData.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <TrendingUp size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">No submitted reports yet</p>
            <p className="text-gray-400">Submit activity reports to see your performance trends here.</p>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6" data-testid="performance-controls">
              <div className="flex flex-wrap gap-2" data-testid="date-range-filter">
                {[
                  { id: 'all', label: 'All Time' },
                  { id: '4w', label: 'Last 4 Weeks' },
                  { id: '8w', label: 'Last 8 Weeks' },
                  { id: '12w', label: 'Last 12 Weeks' },
                ].map(r => (
                  <button
                    key={r.id}
                    onClick={() => setDateRange(r.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      dateRange === r.id ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`range-${r.id}`}
                  >
                    <Calendar size={12} /> {r.label}
                  </button>
                ))}
              </div>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition"
                data-testid="export-csv-btn"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            {/* Performance Score */}
            {latestWeek && (
              <div className="bg-gradient-to-r from-brand-blue to-blue-600 rounded-xl p-5 mb-6 text-white" data-testid="performance-score">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium opacity-80">Performance Score (Latest Week)</h3>
                    <p className="text-3xl font-bold mt-1">
                      {Math.min(100, Math.round(
                        (latestWeek.sales * 20) +
                        (latestWeek.presentations * 5) +
                        (latestWeek.phoneCalls * 1) +
                        (latestWeek.doorKnocks * 1) +
                        (latestWeek.annualizedPremium * 0.01)
                      ))}
                      <span className="text-base font-medium opacity-60">/100</span>
                    </p>
                  </div>
                  <div className="text-right text-xs opacity-80">
                    <p>Sales: {latestWeek.sales} | Pres: {latestWeek.presentations}</p>
                    <p>Premium: ${latestWeek.annualizedPremium.toFixed(0)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="kpi-cards">
              {kpiCards.map(kpi => {
                const current = latestWeek?.[kpi.key] || 0;
                const prev = previousWeek?.[kpi.key] || 0;
                const trend = getTrend(current, prev);
                const TrendIcon = trend.icon;
                const goalVal = goalsMap[latestWeek?.weekStart]?.[kpi.key + 'Goal'] || goalsMap[latestWeek?.weekStart]?.[kpi.key === 'annualizedPremium' ? 'annualizedPremiumGoal' : kpi.key + 'Goal'] || 0;
                return (
                  <div key={kpi.key} className="bg-white rounded-lg shadow-md p-4" data-testid={`kpi-${kpi.key}`}>
                    <p className="text-xs text-gray-500 font-semibold uppercase mb-1">{kpi.label}</p>
                    <p className="text-2xl font-bold" style={{ color: kpi.color }}>
                      {kpi.isCurrency ? `$${current.toFixed(0)}` : current}
                    </p>
                    <div className={`flex items-center gap-1 text-xs mt-1 ${trend.color}`}>
                      <TrendIcon size={12} />
                      <span>{trend.pct} vs prev</span>
                    </div>
                    {hasGoals && goalVal > 0 && (
                      <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                        <Target size={10} />
                        Goal: {kpi.isCurrency ? `$${goalVal}` : goalVal}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Goals vs Actual - Sales & Presentations */}
            {hasGoals && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-md p-6" data-testid="goals-vs-actual-sales">
                  <h3 className="text-lg font-bold text-brand-blue mb-1">Sales: Goal vs Actual</h3>
                  <p className="text-xs text-gray-400 mb-4">Compare your weekly sales against your goals</p>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={goalsVsActualData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="sales" fill="#0047AB" name="Actual Sales" radius={[4,4,0,0]} />
                      <Line type="monotone" dataKey="salesGoal" stroke="#E87722" strokeWidth={2} strokeDasharray="5 5" name="Sales Goal" dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6" data-testid="goals-vs-actual-presentations">
                  <h3 className="text-lg font-bold text-brand-orange mb-1">Presentations: Goal vs Actual</h3>
                  <p className="text-xs text-gray-400 mb-4">Compare your weekly presentations against your goals</p>
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={goalsVsActualData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="presentations" fill="#E87722" name="Actual Presentations" radius={[4,4,0,0]} />
                      <Line type="monotone" dataKey="presentationsGoal" stroke="#0047AB" strokeWidth={2} strokeDasharray="5 5" name="Presentations Goal" dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Sales & Presentations Trend */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-6" data-testid="sales-chart">
                <h3 className="text-lg font-bold text-brand-blue mb-4">Sales Trend</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="sales" stroke="#0047AB" fill="#0047AB" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6" data-testid="presentations-chart">
                <h3 className="text-lg font-bold text-brand-orange mb-4">Presentations Trend</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="presentations" stroke="#E87722" fill="#E87722" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Activity Overview Bar Chart */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="activity-bar-chart">
              <h3 className="text-lg font-bold text-brand-blue mb-4">Weekly Activity Overview</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="doorKnocks" fill="#4169E1" name="Door Knocks" radius={[4,4,0,0]} />
                  <Bar dataKey="phoneCalls" fill="#FFA500" name="Phone Calls" radius={[4,4,0,0]} />
                  <Bar dataKey="presentations" fill="#E87722" name="Presentations" radius={[4,4,0,0]} />
                  <Bar dataKey="sales" fill="#0047AB" name="Sales" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Premium Trend */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="premium-chart">
              <h3 className="text-lg font-bold text-green-600 mb-4">Annualized Premium Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                {hasGoals ? (
                  <ComposedChart data={goalsVsActualData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, '']} />
                    <Legend />
                    <Area type="monotone" dataKey="annualizedPremium" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={2} name="Actual Premium" />
                    <Line type="monotone" dataKey="premiumGoal" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="Premium Goal" dot={{ r: 4 }} />
                  </ComposedChart>
                ) : (
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Premium']} />
                    <Line type="monotone" dataKey="annualizedPremium" stroke="#22c55e" strokeWidth={3} dot={{ r: 5 }} name="Premium" />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* Referrals */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="referrals-chart">
              <h3 className="text-lg font-bold text-purple-600 mb-4">Referrals Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="referralsReceived" fill="#8b5cf6" name="Received" radius={[4,4,0,0]} />
                  <Bar dataKey="referralsContacted" fill="#a78bfa" name="Contacted" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* ADVANCED ANALYTICS SECTION */}
            <div className="border-t-2 border-gray-200 pt-6 mt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1" data-testid="advanced-analytics-header">Advanced Analytics</h2>
              <p className="text-sm text-gray-500 mb-6">Deeper insights into your performance metrics</p>
            </div>

            {/* Conversion & Efficiency Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-6" data-testid="conversion-rate-chart">
                <h3 className="text-lg font-bold text-brand-blue mb-1">Conversion Rate</h3>
                <p className="text-xs text-gray-400 mb-4">Presentations to Sales %</p>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={conversionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => [`${v}%`, '']} />
                    <Legend />
                    <Area type="monotone" dataKey="conversionRate" stroke="#0047AB" fill="#0047AB" fillOpacity={0.12} strokeWidth={2} name="Pres→Sale %" />
                    <Area type="monotone" dataKey="leadsToSale" stroke="#22c55e" fill="#22c55e" fillOpacity={0.08} strokeWidth={2} name="Leads→Sale %" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6" data-testid="efficiency-chart">
                <h3 className="text-lg font-bold text-brand-orange mb-1">Effort Per Sale</h3>
                <p className="text-xs text-gray-400 mb-4">Calls and knocks needed per closed sale</p>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={conversionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="callsPerSale" stroke="#FFA500" strokeWidth={2} name="Calls/Sale" dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="knocksPerSale" stroke="#4169E1" strokeWidth={2} name="Knocks/Sale" dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Cumulative Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-6" data-testid="cumulative-sales-chart">
                <h3 className="text-lg font-bold text-brand-blue mb-1">Cumulative Sales</h3>
                <p className="text-xs text-gray-400 mb-4">Running total over time</p>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={cumulativeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="cumSales" stroke="#0047AB" fill="#0047AB" fillOpacity={0.15} strokeWidth={2} name="Total Sales" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6" data-testid="cumulative-premium-chart">
                <h3 className="text-lg font-bold text-green-600 mb-1">Cumulative Premium</h3>
                <p className="text-xs text-gray-400 mb-4">Running total revenue over time</p>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={cumulativeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => [`$${Number(v).toFixed(0)}`, '']} />
                    <Area type="monotone" dataKey="cumPremium" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} strokeWidth={2} name="Total Premium" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Day-of-Week Performance + Activity Mix */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-6" data-testid="daily-performance-chart">
                <h3 className="text-lg font-bold text-brand-blue mb-1">Day-of-Week Performance</h3>
                <p className="text-xs text-gray-400 mb-4">Average metrics by day of the week</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={dayActivity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="avgSales" fill="#0047AB" name="Avg Sales" radius={[4,4,0,0]} />
                    <Bar dataKey="avgPres" fill="#E87722" name="Avg Presentations" radius={[4,4,0,0]} />
                    <Bar dataKey="avgCalls" fill="#FFA500" name="Avg Calls" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6" data-testid="activity-mix-chart">
                <h3 className="text-lg font-bold text-brand-blue mb-1">Activity Mix</h3>
                <p className="text-xs text-gray-400 mb-4">Distribution of your activities (last 4 weeks)</p>
                {activityMix.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={activityMix} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {activityMix.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">No activity data available</div>
                )}
              </div>
            </div>

            {/* Period Comparison Radar */}
            {radarData.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="period-comparison-chart">
                <h3 className="text-lg font-bold text-brand-blue mb-1">Period Comparison</h3>
                <p className="text-xs text-gray-400 mb-4">Last 4 weeks vs previous 4 weeks (normalized to %)</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis tick={{ fontSize: 10 }} />
                      <Radar name="Last 4 Weeks" dataKey="recent" stroke="#0047AB" fill="#0047AB" fillOpacity={0.3} />
                      <Radar name="Previous 4 Weeks" dataKey="older" stroke="#E87722" fill="#E87722" fillOpacity={0.15} />
                      <Legend />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                  <div className="flex flex-col justify-center gap-3">
                    {['sales', 'presentations', 'doorKnocks', 'phoneCalls', 'leadsWorked'].map(key => {
                      const label = key === 'doorKnocks' ? 'Door Knocks' : key === 'phoneCalls' ? 'Phone Calls' : key === 'leadsWorked' ? 'Leads' : key.charAt(0).toUpperCase() + key.slice(1);
                      const r = recentTotals[key];
                      const o = olderTotals[key];
                      const diff = o > 0 ? (((r - o) / o) * 100).toFixed(0) : r > 0 ? '+100' : '0';
                      const isUp = Number(diff) > 0;
                      const isDown = Number(diff) < 0;
                      return (
                        <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                          <span className="text-sm font-medium text-gray-700">{label}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">{o} → {r}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isUp ? 'bg-green-100 text-green-700' : isDown ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                              {isUp ? '+' : ''}{diff}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </AgentLayout>
  );
};

export default AgentPerformance;
